import { useEffect, useState } from "react";

import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import { usePageNameContext } from "../../../contexts/PageTitleContext";
import useLeadImport, {
  LeadEmailImportItem,
  LeadEmailImportOutcome,
  LeadEmailImportProbeResult,
  LeadEmailImportReport,
  LeadEmailMailboxSnapshot,
} from "../../../data/useLeadImport";

const inputClasses =
  "w-full mt-1 px-3 py-2 text-sm text-gray-900 border-1 border-gray-300 rounded-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

// Colour carries the same meaning everywhere on the page: green happened, amber was deliberately
// refused, red needs somebody to look at a template.
const outcomeStyles: Record<LeadEmailImportOutcome, { label: string; classes: string }> = {
  Imported: { label: "Imported", classes: "bg-green-100 text-green-800" },
  WouldImport: { label: "Would import", classes: "bg-green-100 text-green-800" },
  DuplicateOffer: { label: "Already imported", classes: "bg-amber-100 text-amber-800" },
  DuplicateRecentVrm: { label: "Duplicate registration", classes: "bg-amber-100 text-amber-800" },
  NoRegistration: { label: "No registration found", classes: "bg-red-100 text-red-800" },
  ParseFailed: { label: "Could not parse", classes: "bg-red-100 text-red-800" },
  UnknownSubject: { label: "Subject not recognised", classes: "bg-red-100 text-red-800" },
  ReplyOrForward: { label: "Reply or forward", classes: "bg-gray-100 text-gray-700" },
  Error: { label: "Error", classes: "bg-red-100 text-red-800" },
};

function OutcomeBadge({ outcome }: { outcome: LeadEmailImportOutcome }) {
  const style = outcomeStyles[outcome] ?? { label: outcome, classes: "bg-gray-100 text-gray-700" };

  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${style.classes}`}>
      {style.label}
    </span>
  );
}

function ItemRow({ item }: { item: LeadEmailImportItem }) {
  return (
    <tr className="align-top">
      <td className="px-3 py-2 text-sm font-medium text-gray-900">{item.vrm ?? "—"}</td>
      <td className="px-3 py-2 text-sm text-gray-500">{item.source ?? "—"}</td>
      <td className="px-3 py-2 text-sm text-gray-500">
        <span className="block">{item.subject}</span>
        <span className="block text-xs text-gray-400">
          {item.from}
          {item.received ? ` · ${new Date(item.received).toLocaleString()}` : ""}
        </span>
      </td>
      <td className="px-3 py-2">
        <OutcomeBadge outcome={item.outcome} />
      </td>
      <td className="px-3 py-2 text-sm text-gray-600">
        {item.reason}
        {item.movedToFolder && (
          <span className="block text-xs text-gray-400">Filed in {item.movedToFolder}</span>
        )}
      </td>
    </tr>
  );
}

function ItemsTable({ items }: { items: LeadEmailImportItem[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Reg</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Source</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Email</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Outcome</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Reason</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {items.map(item => (
            <ItemRow key={item.messageId} item={item} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MailboxPanel({ mailbox }: { mailbox: LeadEmailMailboxSnapshot }) {
  if (!mailbox.configured) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4">
        <h2 className="text-sm font-semibold text-red-900">No mailbox configured</h2>
        <p className="mt-1 text-sm text-red-800">
          This environment has no Exchange credentials or shared mailbox address, so nothing can be imported here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {mailbox.error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <h2 className="text-sm font-semibold text-red-900">The mailbox could not be read</h2>
          <p className="mt-1 text-sm text-red-800">{mailbox.error}</p>
          <p className="mt-1 text-sm text-red-800">
            While this is failing the five-minute job imports nothing at all, from any partner.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Unread in inbox</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{mailbox.unreadTotal}</p>
        </div>
        <div className="rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Recognised</p>
          <p className="mt-1 text-2xl font-semibold text-green-600">{mailbox.unreadMatchingSubjects}</p>
        </div>
        <div className="rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Subject not recognised</p>
          <p
            className={`mt-1 text-2xl font-semibold ${
              mailbox.unreadNotMatchingSubjects > 0 ? "text-red-600" : "text-gray-900"
            }`}
          >
            {mailbox.unreadNotMatchingSubjects}
          </p>
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Reading <span className="font-mono">{mailbox.address}</span>. Only unread mail whose subject contains one of{" "}
        {mailbox.matchedSubjects.map(s => `"${s}"`).join(", ")} is ever offered to a parser.
      </p>

      {mailbox.unreadNotMatchingSubjects > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <h2 className="text-sm font-semibold text-amber-900">
            Unread mail the importer does not recognise
          </h2>
          <p className="mt-1 text-sm text-amber-800">
            These are sitting in the inbox and will never be picked up. If a partner has changed its subject line,
            it looks exactly like this - and nothing is logged, filed or retried.
          </p>
          <ul className="mt-2 list-inside list-disc text-sm text-amber-900">
            {mailbox.unmatchedSubjects.map(subject => (
              <li key={subject} className="font-mono">
                {subject}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function LeadImportDiagnostics() {
  const { setInfo } = usePageNameContext();
  const { mailbox, probe, run } = useLeadImport("LeadImportDiagnostics");

  const [vrm, setVrm] = useState("");
  const [probeResult, setProbeResult] = useState<LeadEmailImportProbeResult | null>(null);
  const [probeStatus, setProbeStatus] = useState("Enter a registration to find its email and see where the import stands.");
  const [report, setReport] = useState<LeadEmailImportReport | null>(null);
  const [runStatus, setRunStatus] = useState("A dry run reports on every unread message without importing anything.");
  const [lastRunWasDry, setLastRunWasDry] = useState(true);
  const [lastProbeWasDry, setLastProbeWasDry] = useState(true);

  useEffect(() => {
    setInfo({
      name: "Lead Import",
      desc: "Check why a partner lead did not come through, and re-run the import",
    });
  }, []);

  const snapshot = probeResult?.mailbox ?? report?.mailbox ?? mailbox.data?.data ?? null;
  const probing = probe.isLoading;
  const running = run.isLoading;

  const doProbe = async (dryRun: boolean) => {
    if (!vrm.trim()) return;

    if (!dryRun && !window.confirm(`Import ${vrm.trim().toUpperCase()} now? This creates a lead and files the email.`))
      return;

    setProbeResult(null);
    setLastProbeWasDry(dryRun);
    setProbeStatus(dryRun ? "Searching the mailbox..." : "Importing...");

    try {
      const res = await probe.mutateAsync({ vrm: vrm.trim(), dryRun });
      setProbeResult(res.data ?? null);
      setProbeStatus(res.message ?? "Done.");
    } catch (error: unknown) {
      setProbeStatus(error instanceof Error ? error.message : "The check failed.");
    }
  };

  const doRun = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm("Run the full import live? This creates leads and files every message it reads."))
      return;

    setReport(null);
    setLastRunWasDry(dryRun);
    setRunStatus(dryRun ? "Checking every unread message..." : "Importing...");

    try {
      const res = await run.mutateAsync({ dryRun });
      setReport(res.data ?? null);
      setRunStatus(res.message ?? "Done.");
      mailbox.refetch();
    } catch (error: unknown) {
      setRunStatus(error instanceof Error ? error.message : "The run failed.");
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-lg bg-white p-6 shadow">
        <h2 className="text-lg font-semibold text-gray-900">Mailbox</h2>
        <p className="mt-1 text-sm text-gray-500">
          Partner leads arrive as email. Every five minutes the importer reads the unread mail in this inbox, creates
          the leads, and files each message into Imported, Duplicates or Failed. Mail it does not recognise stays
          unread and silent - which is what this page is for.
        </p>

        <div className="mt-4">
          {mailbox.isLoading ? (
            <p className="text-sm text-gray-500">Reading the mailbox...</p>
          ) : snapshot ? (
            <MailboxPanel mailbox={snapshot} />
          ) : (
            <p className="text-sm text-gray-500">The mailbox could not be read.</p>
          )}
        </div>
      </section>

      <section className="rounded-lg bg-white p-6 shadow">
        <h2 className="text-lg font-semibold text-gray-900">Check one registration</h2>
        <p className="mt-1 text-sm text-gray-500">
          Searches the inbox by content rather than by subject, so it still finds the email when the subject line is
          the thing that changed. Checking creates nothing.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-gray-500" htmlFor="lead-import-vrm">
              Registration
            </label>
            <input
              id="lead-import-vrm"
              className={inputClasses}
              placeholder="Registration"
              value={vrm}
              disabled={probing}
              onChange={e => setVrm(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") doProbe(true);
              }}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <ButtonInput
            label={probing && lastProbeWasDry ? "Checking..." : "Check"}
            onClick={() => doProbe(true)}
            classes=""
            disabled={probing || !vrm.trim()}
            isLoading={probing && lastProbeWasDry}
          />
          <ButtonInput
            label={probing && !lastProbeWasDry ? "Importing..." : "Import this one"}
            onClick={() => doProbe(false)}
            classes=""
            disabled={probing || !vrm.trim()}
            isLoading={probing && !lastProbeWasDry}
          />
          <p className="text-sm text-gray-500">{probeStatus}</p>
        </div>

        {probeResult && !probeResult.found && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <h3 className="text-sm font-semibold text-amber-900">No email found for {probeResult.vrm}</h3>
            <p className="mt-1 text-sm text-amber-800">{probeResult.message}</p>
          </div>
        )}

        {probeResult?.item && (
          <div className="mt-4 space-y-4">
            <ItemsTable items={[probeResult.item]} />

            {probeResult.item.existingCcid && (
              <p className="text-sm text-gray-500">
                Collides with lead <span className="font-mono">{probeResult.item.existingCcid}</span>.
              </p>
            )}

            {probeResult.otherMatches.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Other emails in the inbox for this registration
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Reported only. One accepted offer often arrives more than once, so a single check never imports
                  two leads for the same car.
                </p>
                <div className="mt-2">
                  <ItemsTable items={probeResult.otherMatches} />
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="rounded-lg bg-white p-6 shadow">
        <h2 className="text-lg font-semibold text-gray-900">Full import</h2>
        <p className="mt-1 text-sm text-gray-500">
          The same pass the five-minute job makes, with a reason reported for every message. A dry run leaves the
          mailbox exactly as it found it, so it can be run as often as you like.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <ButtonInput
            label={running && lastRunWasDry ? "Checking..." : "Dry run"}
            onClick={() => doRun(true)}
            classes=""
            disabled={running}
            isLoading={running && lastRunWasDry}
          />
          <ButtonInput
            label={running && !lastRunWasDry ? "Importing..." : "Run live"}
            onClick={() => doRun(false)}
            classes=""
            disabled={running}
            isLoading={running && !lastRunWasDry}
          />
          <p className="text-sm text-gray-500">{runStatus}</p>
        </div>

        {report && (
          <div className="mt-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Examined</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">{report.examined}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">{report.dryRun ? "Would import" : "Imported"}</p>
                <p className="mt-1 text-2xl font-semibold text-green-600">{report.imported}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Duplicates</p>
                <p className="mt-1 text-2xl font-semibold text-amber-600">{report.duplicates}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm text-gray-500">Failed</p>
                <p className="mt-1 text-2xl font-semibold text-red-600">{report.failed}</p>
              </div>
            </div>

            {report.items.length > 0 ? (
              <ItemsTable items={report.items} />
            ) : (
              <p className="text-sm text-gray-500">
                No unread message matched a known subject. If leads are missing, check the unrecognised subjects
                above - the email is in the inbox but the importer cannot see it.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
